import { styled } from '@linaria/react';
import type { TFunction } from 'i18next';
import type { ModelComparisonRow } from '../../../api/agent-miner-client/agent-miner-client.type';
import type { TableColumn } from '../../../components/table/table';
import { inkDark, inkDarkest } from '../../../styles/design-tokens';
import { formatTokens } from './usage.helpers';

/**
 * Builds the Usage table columns: one row per model family with its token
 * breakdown (input / output / cache-read / cache-creation / total) and session
 * count. Token fields are optional at the wire boundary, so each renders an
 * em-dash (via {@link formatTokens}) when absent, and sorts as 0.
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {TableColumn<ModelComparisonRow>[]} Column definitions for `<Table>`
 */
const getUsageColumns = (t: TFunction): TableColumn<ModelComparisonRow>[] => [
  {
    key: 'model_family',
    label: t('agent_miner.usage.model'),
    width: '200px',
    sortable: true,
    render: (_, row) => <FamilyLabel>{row.model_family}</FamilyLabel>,
  },
  {
    key: 'sessions',
    label: t('agent_miner.sessions'),
    width: '90px',
    sortable: true,
    render: (_, row) => <NumberCell>{row.sessions.toLocaleString()}</NumberCell>,
  },
  {
    key: 'total_input_tokens',
    label: t('agent_miner.usage.input_tokens'),
    width: '110px',
    sortable: true,
    sortValue: (row) => row.total_input_tokens ?? 0,
    render: (_, row) => <NumberCell>{formatTokens(row.total_input_tokens)}</NumberCell>,
  },
  {
    key: 'total_output_tokens',
    label: t('agent_miner.usage.output_tokens'),
    width: '110px',
    sortable: true,
    sortValue: (row) => row.total_output_tokens ?? 0,
    render: (_, row) => <NumberCell>{formatTokens(row.total_output_tokens)}</NumberCell>,
  },
  {
    key: 'total_cache_read_tokens',
    label: t('agent_miner.usage.cache_read_tokens'),
    width: '120px',
    sortable: true,
    sortValue: (row) => row.total_cache_read_tokens ?? 0,
    render: (_, row) => <NumberCell>{formatTokens(row.total_cache_read_tokens)}</NumberCell>,
  },
  {
    key: 'total_cache_creation_tokens',
    label: t('agent_miner.usage.cache_creation_tokens'),
    width: '130px',
    sortable: true,
    sortValue: (row) => row.total_cache_creation_tokens ?? 0,
    render: (_, row) => <NumberCell>{formatTokens(row.total_cache_creation_tokens)}</NumberCell>,
  },
  {
    key: 'total_tokens',
    label: t('agent_miner.usage.total_tokens'),
    width: '110px',
    sortable: true,
    sortValue: (row) => row.total_tokens ?? 0,
    render: (_, row) => <TotalCell>{formatTokens(row.total_tokens)}</TotalCell>,
  },
];

const FamilyLabel = styled.span`
  color: ${inkDarkest};
  font-weight: 600;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NumberCell = styled.span`
  color: ${inkDark};
  font-size: 13px;
  font-variant-numeric: tabular-nums;
`;

const TotalCell = styled.span`
  color: ${inkDarkest};
  font-weight: 600;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
`;

export { getUsageColumns };
